'use client';
import React, { useState, useEffect } from 'react';
import AppNav from '@/components/AppNav';

import SafetyAndFooter from '@/components/SafetyAndFooter';
import { MemberProfile } from '@/lib/supabase/types';
import { jsnService } from '@/lib/supabase/jsnService';
import { createClient } from '@/lib/supabase/client';
import { Heart, Users, MessageCircle, Lock, Crown, ArrowRight, Clock, AlertTriangle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import Link from 'next/link';
import { getPublicDisplayName } from '@/lib/supabase/nameUtils';

type MatchTab = 'mutual' | 'liked' | 'liked-me';

export default function MatchesScreen() {
  const [activeTab, setActiveTab] = useState<MatchTab>('mutual');
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [likedByIds, setLikedByIds] = useState<Set<string>>(new Set());
  const [mutualIds, setMutualIds] = useState<Set<string>>(new Set());
  const [allProfiles, setAllProfiles] = useState<MemberProfile[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [newMatchId, setNewMatchId] = useState<string | null>(null);
  const [photoStatus, setPhotoStatus] = useState<string | undefined>(undefined);
  const [isPhotoBlocked, setIsPhotoBlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [whoLikedYouOpen, setWhoLikedYouOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { window.location.href = '/sign-up-login-screen'; return; }

      // Email verification gate
      if (!user.email_confirmed_at) {
        window.location.href = '/verify-email';
        return;
      }

      const [up, mp] = await Promise.all([
        jsnService.getUserProfileById(user.id),
        jsnService.getCurrentMemberProfile(),
      ]);

      if (up?.accountStatus === 'suspended') { window.location.href = '/suspended'; return; }

      // ── Photo-review gate ──────────────────────────────────────────────
      // Block access until the profile photo is approved and account is active.
      // Admins bypass this gate. /discover-page renders the existing
      // "Photo Pending Review" UI when photoStatus is pending.
      const isAdmin = up?.role === 'admin';
      if (!isAdmin && (mp?.photoStatus !== 'approved' || up?.accountStatus !== 'active')) {
        window.location.href = '/discover-page';
        return;
      }
      // ──────────────────────────────────────────────────────────────────

      setCurrentUserId(user.id);
      setIsPremium(up?.membershipTier === 'premium' || false);

      const status = mp?.photoStatus;
      setPhotoStatus(status);
      setIsPhotoBlocked(status === 'pending' || status === 'rejected');

      const [liked, likedBy, profiles] = await Promise.all([
        jsnService.getLikedProfileIds(user.id),
        jsnService.getLikedByProfileIds(user.id),
        jsnService.getAllMemberProfiles(),
      ]);

      const likedSet = new Set(liked);
      const likedBySet = new Set(likedBy);
      const mutualSet = new Set([...liked].filter(id => likedBySet.has(id)));

      setLikedIds(likedSet);
      setLikedByIds(likedBySet);
      setMutualIds(mutualSet);
      setAllProfiles(profiles);
      setIsLoading(false);
    };
    load();
  }, []);

  const likedProfiles = allProfiles.filter(p => likedIds.has(p.id));
  const likedByProfiles = allProfiles.filter(p => likedByIds.has(p.id));
  const mutualProfiles = allProfiles.filter(p => mutualIds.has(p.id));

  const handleLikeBack = async (profile: MemberProfile) => {
    if (!currentUserId) return;
    await jsnService.likeBack(currentUserId, profile.id);
    setLikedIds(prev => new Set([...prev, profile.id]));
    setMutualIds(prev => new Set([...prev, profile.id]));
    setNewMatchId(profile.id);
    const name = getPublicDisplayName({ firstName: profile.firstName, lastName: profile.lastName, displayName: profile.displayName });
    toast.success(`🎉 It's a Match with ${name}!`, { description: 'You can now message each other.' });
    setTimeout(() => setNewMatchId(null), 3000);
  };

  const tabs: { key: MatchTab; label: string; count: number }[] = [
    { key: 'mutual', label: 'Mutual Matches', count: mutualProfiles.length },
    { key: 'liked', label: 'I Liked', count: likedProfiles.length },
  ];

  const currentProfiles =
    activeTab === 'mutual' ? mutualProfiles :
    activeTab === 'liked' ? likedProfiles :
    likedByProfiles;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isPhotoBlocked) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <AppNav activePage="matches" />
        <div className="pt-14 flex items-center justify-center min-h-screen px-4">
          <div className="max-w-md w-full text-center">
            {photoStatus === 'pending' ? (
              <div className="bg-[#FFBF00]/10 border border-[#FFBF00]/25 rounded-2xl p-8 space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#FFBF00]/15 flex items-center justify-center mx-auto">
                  <Clock size={28} style={{ color: '#FFBF00' }} />
                </div>
                <h2 className="text-xl font-bold text-foreground">Photo Pending Review</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your profile photo is pending review. You&apos;ll be able to browse and interact with members once your profile is approved.
                </p>
                <Link href="/account-settings" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold inline-block">
                  View Account Settings
                </Link>
              </div>
            ) : (
              <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 space-y-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <AlertTriangle size={28} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold text-foreground">Photo Rejected</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your profile photo was rejected. Please upload a new clear profile photo for review.
                </p>
                <Link href="/discover-page" className="btn-primary px-6 py-3 rounded-xl text-sm font-semibold inline-block">
                  Upload New Photo
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppNav activePage="matches" />
      <div className="pt-14 max-w-screen-lg mx-auto px-4 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-black text-foreground">Your Matches</h1>
          <p className="text-muted-foreground text-sm mt-1">Connect with Kenyan singles who share your interest</p>
        </div>

        {/* Who Liked You Drawer */}
        <div className="mb-6 rounded-2xl border border-border overflow-hidden">
          <button
            onClick={() => {
              if (!isPremium) {
                toast.info('Upgrade to Premium to see who liked you.', {
                  description: 'Upgrade to Premium to unlock messaging, matches, and more.',
                  action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
                  duration: 4000,
                });
                return;
              }
              setWhoLikedYouOpen(prev => !prev);
            }}
            className="w-full flex items-center justify-between px-5 py-4 bg-card hover:bg-muted/20 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(0,128,0,0.15)' }}>
                <Heart size={16} style={{ color: '#008000' }} />
              </div>
              <div className="text-left">
                <div className="text-sm font-bold text-foreground flex items-center gap-2">
                  Who Liked You
                  {likedByProfiles.length > 0 && isPremium && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white" style={{ backgroundColor: '#008000' }}>
                      {likedByProfiles.length}
                    </span>
                  )}
                  {!isPremium && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-[#FFBF00]/20 text-[#FFBF00] border border-[#FFBF00]/30">
                      Premium
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {isPremium ? 'Members who have liked your profile' : 'Upgrade to see who liked you'}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isPremium && <Lock size={14} className="text-muted-foreground" />}
              {isPremium && (whoLikedYouOpen ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />)}
            </div>
          </button>

          {/* Drawer content — premium only */}
          {isPremium && whoLikedYouOpen && (
            <div className="border-t border-border bg-background/50 p-5">
              {likedByProfiles.length === 0 ? (
                <div className="text-center py-8 space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-muted/30 flex items-center justify-center mx-auto">
                    <Heart size={22} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">No likes yet</p>
                  <p className="text-xs text-muted-foreground">Keep your profile complete to attract more attention.</p>
                  <Link href="/discover-page" className="btn-primary px-5 py-2 rounded-xl text-xs font-semibold inline-flex items-center gap-1.5">
                    Browse Profiles
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {likedByProfiles.map(profile => (
                    <WhoLikedYouCard
                      key={profile.id}
                      profile={profile}
                      isMutual={mutualIds.has(profile.id)}
                      onLikeBack={() => handleLikeBack(profile)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Upgrade gate — free users */}
          {!isPremium && (
            <div className="border-t border-border bg-background/50 px-5 py-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex -space-x-2">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-9 h-9 rounded-full bg-muted/50 border-2 border-card flex items-center justify-center">
                      <Lock size={12} className="text-muted-foreground" />
                    </div>
                  ))}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <p className="text-sm font-semibold text-foreground">Members have liked your profile</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Upgrade to Premium to see who liked you and start connecting.</p>
                </div>
                <Link
                  href="/membership"
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-semibold text-white flex-shrink-0"
                  style={{ backgroundColor: '#750000' }}
                >
                  <Crown size={13} />Upgrade
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Tabs — mutual + I Liked (free users can see both) */}
        <div className="flex bg-muted/20 rounded-xl p-1 mb-8 border border-border gap-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                activeTab === tab.key ? 'tab-active' : 'tab-inactive'
              }`}
            >
              <span className="hidden sm:inline">{tab.label}</span>
              <span className="sm:hidden">{tab.key === 'mutual' ? 'Mutual' : 'I Liked'}</span>
              {tab.count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                  activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-primary/20 text-primary'
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {newMatchId && (
          <div className="mb-6 p-4 rounded-xl flex items-center gap-3 fade-in border" style={{ backgroundColor: 'rgba(0,128,0,0.1)', borderColor: 'rgba(0,128,0,0.3)' }}>
            <Heart size={20} style={{ color: '#008000', fill: '#008000' }} className="flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">New Match!</p>
              <p className="text-xs text-muted-foreground">You and {getPublicDisplayName({ firstName: allProfiles.find(p => p.id === newMatchId)?.firstName, lastName: allProfiles.find(p => p.id === newMatchId)?.lastName, displayName: allProfiles.find(p => p.id === newMatchId)?.displayName })} are now matched.</p>
            </div>
          </div>
        )}

        {currentProfiles.length === 0 ? (
          <EmptyState tab={activeTab} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {currentProfiles.map(profile => (
              <MatchCard
                key={profile.id}
                profile={profile}
                tab={activeTab}
                isMutual={mutualIds.has(profile.id)}
                isPremium={isPremium}
                onLikeBack={() => handleLikeBack(profile)}
              />
            ))}
          </div>
        )}
      </div>

      <SafetyAndFooter />
    </div>
  );
}

function WhoLikedYouCard({ profile, isMutual, onLikeBack }: {
  profile: MemberProfile;
  isMutual: boolean;
  onLikeBack: () => void;
}) {
  const publicName = getPublicDisplayName({ firstName: profile.firstName, lastName: profile.lastName, displayName: profile.displayName });
  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3 hover:border-primary/40 transition-colors">
      {isMutual && (
        <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#008000' }}>
          <Heart size={11} style={{ fill: '#008000', color: '#008000' }} />It&apos;s a Match!
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${profile.avatarColor}, ${profile.avatarColor}99)` }}>
          {profile.photoUrl ? (
            <img src={profile.photoUrl} alt={`${publicName} profile`} className="w-full h-full object-cover" />
          ) : profile.avatarInitials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-foreground text-sm truncate">{publicName}, {profile.age}</div>
          <div className="text-xs text-muted-foreground truncate">{profile.usState} · {profile.kenyanCounty}</div>
        </div>
      </div>
      <div className="flex gap-2">
        {isMutual ? (
          <button onClick={() => { window.location.href = '/messages'; }} className="flex-1 btn-primary py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5">
            <MessageCircle size={12} />Message
          </button>
        ) : (
          <button onClick={onLikeBack} className="flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 text-white transition-colors" style={{ backgroundColor: '#008000' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#006600')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#008000')}
          >
            <Heart size={12} />Like Back
          </button>
        )}
      </div>
    </div>
  );
}

function MatchCard({ profile, tab, isMutual, isPremium, onLikeBack }: {
  profile: MemberProfile;
  tab: MatchTab;
  isMutual: boolean;
  isPremium: boolean;
  onLikeBack: () => void;
}) {
  const publicName = getPublicDisplayName({ firstName: profile.firstName, lastName: profile.lastName, displayName: profile.displayName });

  const handleMessage = () => {
    if (isPremium) {
      window.location.href = '/messages';
    } else {
      toast.info('Upgrade to Premium to send messages', {
        description: 'Upgrade to Premium to unlock messaging and more features.',
        action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
      });
    }
  };

  return (
    <div className={`bg-card border rounded-2xl p-5 space-y-4 transition-all duration-200 hover:-translate-y-1 ${
      isMutual ? 'border-primary/50 shadow-[0_0_20px_rgba(117,0,0,0.15)]' : 'border-border hover:border-primary/40'
    }`}>
      {isMutual && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
          <Heart size={12} className="fill-primary" />It&apos;s a Match!
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0 overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${profile.avatarColor}, ${profile.avatarColor}99)` }}>
          {profile.photoUrl ? (
            <img src={profile.photoUrl} alt={`${publicName} profile`} className="w-full h-full object-cover" />
          ) : profile.avatarInitials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-foreground text-sm truncate">{publicName}, {profile.age}</div>
          <div className="text-xs text-muted-foreground truncate">{profile.usState} · {profile.kenyanCounty}</div>
          <div className="text-xs text-muted-foreground truncate">{profile.intention}</div>
        </div>
      </div>
      <div className="flex gap-2">
        {isMutual ? (
          <button onClick={handleMessage} className="flex-1 btn-primary py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5">
            <MessageCircle size={13} />Message
          </button>
        ) : tab === 'liked-me' ? (
          <button onClick={onLikeBack} className="flex-1 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5 text-white" style={{ backgroundColor: '#008000' }}>
            <Heart size={13} />Like Back
          </button>
        ) : (
          <button onClick={handleMessage} className="flex-1 btn-outline-red py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1.5">
            <MessageCircle size={13} />Message
          </button>
        )}
        <Link href="/discover-page" className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-muted/20 border border-border text-muted-foreground hover:text-foreground transition-colors">
          <ArrowRight size={13} />View
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ tab }: { tab: MatchTab }) {
  return (
    <div className="text-center py-16 space-y-4">
      <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center mx-auto">
        {tab === 'mutual' ? <Users size={28} className="text-muted-foreground" /> :
         tab === 'liked' ? <Heart size={28} className="text-muted-foreground" /> :
         <Lock size={28} className="text-muted-foreground" />}
      </div>
      <div>
        <p className="font-semibold text-foreground">
          {tab === 'mutual' ? 'No mutual matches yet' : tab === 'liked' ? 'No liked profiles yet' : 'No one has liked you yet'}
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          {tab === 'mutual' ? 'Like profiles and wait for them to like you back.' : tab === 'liked' ? 'Start liking profiles in Discover.' : 'Keep your profile complete to attract more attention.'}
        </p>
      </div>
      <Link href="/discover-page" className="btn-primary px-6 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2">
        Browse Profiles
      </Link>
    </div>
  );
}
